import {
  Info,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import * as styles from './AcrCloudCredentials.styles';

export interface AcrCloudCredentialsProps {
  onSubmit: (accessKey: string, secretKey: string) => Promise<void>;
  loading?: boolean;
  existingCredentials?: {
    id: number;
    secretId: string;
    description: string;
    createDate: string;
    application: {
      id: number;
      displayName: string;
    };
  } | null;
}

export const AcrCloudCredentials = ({ onSubmit, loading = false, existingCredentials }: AcrCloudCredentialsProps) => {
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Populate form fields when existing credentials are available
  useEffect(() => {
    if (existingCredentials && !isEditing) {
      // Note: We can't display the actual secret key for security reasons
      // The secret key is stored securely and we only show that credentials exist
      setAccessKey('*** (credentials already configured)');
      setSecretKey('*** (credentials already configured)');
    } else if (!existingCredentials) {
      setAccessKey('');
      setSecretKey('');
    }
  }, [existingCredentials, isEditing]);

  const handleFieldFocus = () => {
    if (existingCredentials && !isEditing) {
      setIsEditing(true);
      setAccessKey('');
      setSecretKey('');
    }
  };

  const handleSubmit = async () => {
    if (!accessKey.trim() || !secretKey.trim()) {
      return;
    }
    
    try {
      await onSubmit(accessKey.trim(), secretKey.trim());
      // Clear form on success and exit editing mode
      setAccessKey('');
      setSecretKey('');
      setIsEditing(false);
    } catch (error) {
      // Error handling is done in parent component
      console.error('Failed to submit ACR Cloud credentials:', error);
    }
  };

  const isSubmitDisabled = !accessKey.trim() || !secretKey.trim() || loading || (!!existingCredentials && !isEditing);

  return (
    <Card sx={styles.card}>
      <CardContent sx={styles.cardContent}>
        <Typography
          variant="h6"
          color="text.primary"
          sx={styles.title}
        >
          ACRCloud Keys
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={styles.description}
        >
          {existingCredentials && !isEditing
            ? `ACR Cloud credentials are already configured for ${existingCredentials.application.displayName}. Created on ${new Date(existingCredentials.createDate).toLocaleDateString()}. Click into the fields above to replace them.`
            : existingCredentials && isEditing
            ? `Replacing ACR Cloud credentials for ${existingCredentials.application.displayName}. The old credentials will be deactivated.`
            : 'Please paste your Secret and Access keys into the fields below.'
          }
        </Typography>

        <Box sx={styles.fieldContainer}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={styles.fieldLabel}
          >
            Access Key
          </Typography>
          <TextField
            fullWidth
            value={accessKey}
            onChange={(e) => setAccessKey(e.target.value)}
            onFocus={handleFieldFocus}
            placeholder="Enter your ACRCloud Access Key"
            sx={styles.textField}
            disabled={loading}
          />
        </Box>

        <Box sx={styles.fieldContainer}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={styles.fieldLabel}
          >
            Secret Key
          </Typography>
          <TextField
            fullWidth
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            onFocus={handleFieldFocus}
            placeholder="Enter your ACRCloud Secret Key"
            sx={styles.textField}
            disabled={loading}
          />
        </Box>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={styles.submitButton}
        >
          {existingCredentials && !isEditing 
            ? 'Credentials Already Configured' 
            : existingCredentials && isEditing 
            ? 'Replace Credentials' 
            : 'Submit'}
        </Button>

        <Alert
          severity="info"
          icon={<Info />}
          sx={styles.infoAlert}
        >
          Please make sure that you're referencing the keys for the "identify-us-west-2.acrcloud.com" host and the "Audio Fingerprinting & Cover Song Identification" audio engine.
        </Alert>
      </CardContent>
    </Card>
  );
};
